import { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';
import { useDebouncedCallback } from 'use-debounce';

import { CsvUpload } from 'src/components/CsvUpload';
import {
  CUSTOM_BUTTON_SIZE,
  CUSTOM_BUTTON_VARIANT,
  CustomButton,
} from 'src/components/CustomButton';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { Loader } from 'src/components/Loader';
import { PROMPT_INSTRUCTIONS_ACTION, PromptInstructions } from 'src/components/PromptInstructions';
import { replaceFilePlaceholders } from 'src/components/RichSlate/lib';
import { StaticPanel } from 'src/components/StaticPanel';
import { getCreatedResourceMessage } from 'src/data/performance';
import { TEMPLATE_BUILDER_PARAMS } from 'src/data/template';
import { EVENT_PAGE } from 'src/lib/events';
import { usePreparePromptInstructionsProps } from 'src/services/prompts';
import { useNotificationActions } from 'src/store';

import { useMultiPromptTemplate, useMultiPromptTemplateFieldsData } from '../../../../services';
import { useLocalActions } from '../../store';
import { BlockPersonas } from '../BlockPersonas';

import { TextArea } from './components/TextArea';
import { useEvents } from './useEvents';
import { useStyles } from './useStyles';
import { useViewState } from './useViewState';

export const BlockMultiPrompt = ({
  inGenerationPanel,
  onChangeSettings,
  className,
  onEdit,
  customTemplateId,
}) => {
  const styles = useStyles();
  const localActions = useLocalActions();
  const notificationActions = useNotificationActions();
  const [plusButtonRef, setPlusButtonRef] = useState(false);
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);
  const [uploadingDocumentIndex, setUploadingDocumentIndex] = useState(null);
  const {
    multiPromptFields,
    generationTool,
    generating,
    multiPromptFieldImages,
    multiPromptFieldDocuments,
    currentUrls,
    uploadingDocuments,
    uploadingImages,
    uploadedCsv,
    isCsvLoading,
    language,
  } = useViewState();

  const multiPromptTemplate = useMultiPromptTemplate(customTemplateId);
  const multiPromptFieldData = useMultiPromptTemplateFieldsData({
    generationTool,
    customTemplateId,
  });
  const isCsvTemplate = multiPromptTemplate?.templateType === TEMPLATE_BUILDER_PARAMS.csv;

  // Init local state from the "custom template" data
  useEffect(() => {
    if (multiPromptFields && Object.keys(multiPromptFields).length > 0) {
      return;
    }

    localActions.initMultiPromptFields(multiPromptFieldData);
  }, [localActions, multiPromptFieldData, multiPromptFields]);

  const handlePromptInstructionsAction = useCallback(
    (action, data) => {
      switch (action) {
        case PROMPT_INSTRUCTIONS_ACTION.insertImage: {
          localActions.setMultiPromptFieldImages(data.item, data.randomId, data.index);
          const { id, ...restProps } = data.item;
          localActions.addPromptImage(id, restProps, data.randomId);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.insertDocument:
        case PROMPT_INSTRUCTIONS_ACTION.insertAudio:
        case PROMPT_INSTRUCTIONS_ACTION.insertVideo: {
          localActions.setMultiPromptFieldDocuments(data.item, null, data.randomId, data.index);
          const { id, ...restProps } = document;
          localActions.addPromptDocument(id, restProps, data.randomId);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removePromptImage: {
          localActions.removeIdFromMultiPromptFieldImages(data.itemId, data.index);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removeUploadingImage: {
          localActions.removeUploadingImage(data.itemId);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removePromptDocument: {
          localActions.removeIdFromMultiPromptFieldDocuments(data.itemId, data.index);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removeUploadingDocument: {
          localActions.removeUploadingDocument(data.itemId);
          break;
        }

        default:
          break;
      }
    },
    [localActions]
  );

  const setIsUploadingImage = useCallback(
    (isUploadingImage, randomId, index) => {
      localActions.setIsUploadingImage(isUploadingImage);
      setUploadingImageIndex(index);
      if (isUploadingImage && randomId) {
        localActions.addUploadingImage(randomId);
        localActions.setMultiPromptFieldImages(null, randomId, index);
      }
    },
    [localActions]
  );

  const setIsUploadingDocument = useCallback(
    (isUploadingDocument, data) => {
      localActions.setIsUploadingDocument(isUploadingDocument);
      setUploadingDocumentIndex(data?.index);
      if (isUploadingDocument && data?.randomId) {
        localActions.addUploadingDocument(data?.randomId, data?.file?.name, data?.contentType);
        localActions.setMultiPromptFieldDocuments(
          null,
          data?.file?.name,
          data?.randomId,
          data?.index
        );
      }
    },
    [localActions]
  );

  const setDocumentError = useCallback(
    (id, error) => {
      localActions.updatePromptDocumentError(id, error);
      notificationActions.displayError(error);
    },
    [localActions, notificationActions]
  );

  const setImageError = useCallback(
    (id, error) => {
      localActions.updatePromptImageErrors(id, error);
      notificationActions.displayError(error);
    },
    [localActions, notificationActions]
  );

  const setImageUploadSuccess = useCallback(
    (image) => {
      notificationActions.displaySuccess(getCreatedResourceMessage(image));
    },
    [notificationActions]
  );

  const setDocumentUploadSuccess = useCallback(
    (document) => {
      notificationActions.displaySuccess(getCreatedResourceMessage(document));
    },
    [notificationActions]
  );

  const clearImageError = useCallback(() => {
    localActions.clearPromptImageErrors();
  }, [localActions]);

  const clearDocumentError = useCallback(() => {
    localActions.clearPromptDocumentErrors();
  }, [localActions]);

  const promptInstructionsProps = usePreparePromptInstructionsProps({
    eventPage: EVENT_PAGE.editor,
    generationTool,
    uploadingImages,
    uploadingDocuments,
    currentUrls,
    provideUrlResources: true,
    provideDocumentResources: true,
    provideImageResources: true,
    setIsUploadingDocument,
    setIsUploadingImage,
    setDocumentError,
    setImageError,
    clearDocumentError,
    clearImageError,
    setDocumentUploadSuccess,
    setImageUploadSuccess,
    onAction: handlePromptInstructionsAction,
    displayEditBrief: true,
  });

  const events = useEvents();

  const shouldExtractBriefFromUrlDebounced = useDebouncedCallback(() => {
    localActions.shouldExtractUrlBrief();
  }, 350);

  const handleSettingsChange = useCallback(
    (vars) => {
      onChangeSettings(vars);
      events.trackMultiPromptChangeBrief();
    },
    [onChangeSettings, events]
  );

  const handleDrop = (csvFile) => {
    if (onEdit && !isEqual(csvFile, uploadedCsv)) {
      onEdit();
    }
    localActions.uploadCsv(csvFile);
  };

  const handleDelete = () => {
    localActions.setUploadedCsv(null);
  };

  if (!multiPromptFields || Object.keys(multiPromptFields).length === 0) {
    return <Loader className={styles.loader} />;
  }

  return (
    <StaticPanel className={styles.wrapper}>
      <div className={cx(styles.root, className)}>
        {inGenerationPanel && (
          <div className={styles.title}>
            <CustomTypography variant={TYPOGRAPHY_VARIANT.medium14}>Brief</CustomTypography>

            <CustomButton
              onClick={handleSettingsChange}
              disabled={generating}
              variant={CUSTOM_BUTTON_VARIANT.outlined}
              size={CUSTOM_BUTTON_SIZE.small}
            >
              Change brief
            </CustomButton>
          </div>
        )}

        {isCsvTemplate ? (
          <div className={cx({ [styles.csvUpload]: !inGenerationPanel || uploadedCsv })}>
            <CsvUpload
              csvFile={uploadedCsv}
              onDelete={handleDelete}
              onDrop={handleDrop}
              isLoading={isCsvLoading}
              hideEmptyState={inGenerationPanel && !uploadedCsv}
              displayOnly={inGenerationPanel}
            />
          </div>
        ) : (
          multiPromptFieldData?.order?.map((field, index) => {
            const fieldHtml = multiPromptFields[field]?.html
              ? multiPromptFields[field].html
              : multiPromptFields[field];
            let fieldSerialized = multiPromptFields[field]?.text
              ? multiPromptFields[field].text
              : '';
            if (
              multiPromptFields[field]?.placeholders &&
              Object.keys(multiPromptFields[field].placeholders).length
            ) {
              fieldSerialized = replaceFilePlaceholders(fieldSerialized, multiPromptFields, field);
            }

            return inGenerationPanel ? (
              <div className={styles.briefRowGenPanel}>
                <TextArea
                  title={multiPromptFieldData.titles[field]}
                  body={fieldSerialized}
                  disabled={inGenerationPanel}
                />
              </div>
            ) : (
              <>
                <div className={styles.fieldTitle}>{multiPromptFieldData.titles[field]}</div>
                <PromptInstructions
                  className={styles.promptField}
                  classes={{ input_root: styles.textArea }}
                  editableClasses={{ editable: styles.editable }}
                  {...promptInstructionsProps}
                  value={fieldHtml}
                  placeholder={multiPromptFieldData.placeholders[field]}
                  onChange={(name, value) => {
                    if (onEdit && !isEqual(value, localActions.getMultiPromptField(field))) {
                      onEdit();
                    }
                    localActions.setMultiPromptField(field, { ...value, index });
                    shouldExtractBriefFromUrlDebounced();
                  }}
                  generationTool={generationTool}
                  isFirstField={index === 0}
                  hideActions
                  shortExpanding
                  noTags
                  plusButtonRef={plusButtonRef}
                  setPlusButtonRef={setPlusButtonRef}
                  isUploadingImage={uploadingImageIndex === index}
                  isUploadingDocument={uploadingDocumentIndex === index}
                  index={index}
                  uploadingImages={multiPromptFieldImages[index]}
                  uploadingDocuments={multiPromptFieldDocuments[index]}
                  isPromptField={false}
                />
              </>
            );
          })
        )}

        <BlockPersonas
          visibleCount={5}
          disabledCollapsed={inGenerationPanel}
          className={inGenerationPanel ? styles.rowPanel : styles.rowPersonas}
          titleBold={!inGenerationPanel}
          generationTool={generationTool}
          language={language}
        />
      </div>
    </StaticPanel>
  );
};
